// BEAM UI primitive — CalendarField.
// A pure-JavaScript date picker: no native module (no @react-native-community/
// datetimepicker), so it ships as an over-the-air JS update onto any installed
// binary. The trigger shows the selected date (or a placeholder); tapping it opens
// a bottom-sheet Modal with a tappable month calendar grid. Picking a day calls
// onChange and closes the sheet.
//
// The sheet mirrors Dropdown EXACTLY (a real-device fix): a definite-height root so
// maxHeight resolves and the sheet anchors to the bottom, a safe-area bottom inset,
// a tap-to-close scrim, and the keyboard dismissed on open. Theme tokens only; the
// sole raw literals are grid layout math (flexBasis percentages).
import React, { useState } from 'react';
import { Keyboard, Modal, Pressable, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { theme } from '../../theme';
import { Text } from './Text';

export interface CalendarFieldProps {
  label?: string;
  value: Date | null;
  onChange: (date: Date) => void;
  placeholder?: string; // default 'Select a date'
  accessibilityLabel?: string; // for the trigger; falls back to label/placeholder
}

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;
// Sunday-first weekday initials. Keyed by index (S and T repeat).
const WEEKDAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;

type ViewMonth = { year: number; month: number };

function viewFrom(date: Date | null): ViewMonth {
  const base = date ?? new Date();
  return { year: base.getFullYear(), month: base.getMonth() };
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

// ISO 'YYYY-MM-DD' for a specific calendar day (local), used as each cell's
// accessibilityLabel so tests (and screen readers) can target a specific day.
function isoDay(year: number, month: number, day: number): string {
  return `${year}-${pad2(month + 1)}-${pad2(day)}`;
}

// Display label for the trigger, e.g. 'Jul 18, 2026'.
function formatDisplay(d: Date): string {
  return `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function sameDay(d: Date, year: number, month: number, day: number): boolean {
  return d.getFullYear() === year && d.getMonth() === month && d.getDate() === day;
}

export function CalendarField({
  label,
  value,
  onChange,
  placeholder = 'Select a date',
  accessibilityLabel,
}: CalendarFieldProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<ViewMonth>(() => viewFrom(value));
  const insets = useSafeAreaInsets();

  const triggerText = value ? formatDisplay(value) : placeholder;
  const triggerA11y = value ? formatDisplay(value) : (accessibilityLabel ?? label ?? placeholder);

  function openSheet() {
    // Re-anchor the grid to the selected month (or this month) each time it opens.
    setView(viewFrom(value));
    // A picker has no text input of its own; drop any open keyboard so the bottom
    // sheet is not rendered behind it (real-device occlusion the emulator hid).
    Keyboard.dismiss();
    setOpen(true);
  }

  // Month arithmetic via Date normalization handles year rollover for free.
  function shiftMonth(delta: number) {
    setView((v) => {
      const d = new Date(v.year, v.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  }

  function selectDay(day: number) {
    // Noon local time avoids DST / midnight edge cases shifting the calendar day.
    onChange(new Date(view.year, view.month, day, 12, 0, 0, 0));
    setOpen(false);
  }

  const today = new Date();
  const firstWeekday = new Date(view.year, view.month, 1).getDay(); // 0 = Sunday
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
  // Leading blanks pad the first row so day 1 lands under its weekday column.
  const cells: Array<number | null> = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);

  return (
    <View style={styles.container}>
      {label ? (
        <Text variant="small" color="secondary">
          {label}
        </Text>
      ) : null}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={triggerA11y}
        onPress={openSheet}
        style={({ pressed }) => [styles.trigger, pressed && styles.triggerPressed]}
      >
        <Text variant="body" color={value ? 'primary' : 'secondary'} numberOfLines={1} style={styles.triggerText}>
          {triggerText}
        </Text>
      </Pressable>

      <Modal visible={open} transparent animationType="slide" onRequestClose={() => setOpen(false)}>
        {/* Full-screen container with a definite height so the sheet's maxHeight resolves
            and it anchors to the bottom (mirrors the fixed Dropdown / GlobalAssistantDock sheet). */}
        <View style={styles.modalRoot}>
          <Pressable
            style={styles.scrim}
            accessibilityRole="button"
            accessibilityLabel="Close"
            onPress={() => setOpen(false)}
          />
          <View style={[styles.sheet, { paddingBottom: insets.bottom + theme.spacing.md }]}>
            {label ? (
              <Text variant="subhead" numberOfLines={1}>
                {label}
              </Text>
            ) : null}

            {/* Month navigation: prev arrow, month + year, next arrow. */}
            <View style={styles.navRow}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Previous month"
                onPress={() => shiftMonth(-1)}
                style={({ pressed }) => [styles.navArrow, pressed && styles.navArrowPressed]}
              >
                <Text variant="subhead" color="link">
                  {'‹'}
                </Text>
              </Pressable>
              <Text variant="subhead" numberOfLines={1}>
                {`${MONTHS_LONG[view.month]} ${view.year}`}
              </Text>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Next month"
                onPress={() => shiftMonth(1)}
                style={({ pressed }) => [styles.navArrow, pressed && styles.navArrowPressed]}
              >
                <Text variant="subhead" color="link">
                  {'›'}
                </Text>
              </Pressable>
            </View>

            {/* Weekday header letters. */}
            <View style={styles.weekRow}>
              {WEEKDAY_INITIALS.map((letter, i) => (
                <View key={i} style={styles.weekCell}>
                  <Text variant="small" color="secondary">
                    {letter}
                  </Text>
                </View>
              ))}
            </View>

            {/* Day grid. */}
            <View style={styles.grid}>
              {cells.map((day, i) => {
                if (day === null) {
                  return <View key={`blank-${i}`} style={styles.cell} />;
                }
                const selected = value !== null && sameDay(value, view.year, view.month, day);
                const isToday = sameDay(today, view.year, view.month, day);
                const dayColor = selected ? 'onPrimary' : isToday ? 'link' : 'primary';
                return (
                  <Pressable
                    key={isoDay(view.year, view.month, day)}
                    accessibilityRole="button"
                    accessibilityLabel={isoDay(view.year, view.month, day)}
                    accessibilityState={{ selected }}
                    onPress={() => selectDay(day)}
                    style={styles.cell}
                  >
                    <View
                      style={[
                        styles.dayBox,
                        selected && styles.dayBoxSelected,
                        !selected && isToday && styles.dayBoxToday,
                      ]}
                    >
                      <Text variant="body" color={dayColor}>
                        {String(day)}
                      </Text>
                    </View>
                  </Pressable>
                );
              })}
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { gap: theme.spacing.xs },
  trigger: {
    minHeight: theme.layout.fieldHeight,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.color.bg.surface,
    borderRadius: theme.radius.sm,
    borderWidth: theme.layout.hairline,
    borderColor: theme.color.border.strong,
    paddingHorizontal: theme.spacing.md,
    gap: theme.spacing.sm,
  },
  triggerPressed: { backgroundColor: theme.color.bg.subtle },
  triggerText: { flex: 1 },
  modalRoot: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  scrim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: theme.color.scrim,
  },
  sheet: {
    backgroundColor: theme.color.bg.surface,
    borderTopLeftRadius: theme.radius.lg,
    borderTopRightRadius: theme.radius.lg,
    paddingHorizontal: theme.spacing.md,
    paddingTop: theme.spacing.lg,
    gap: theme.spacing.sm,
    maxHeight: '80%',
  },
  navRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  navArrow: {
    minWidth: theme.layout.minTapTarget,
    minHeight: theme.layout.minTapTarget,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.radius.sm,
  },
  navArrowPressed: { backgroundColor: theme.color.bg.subtle },
  weekRow: {
    flexDirection: 'row',
  },
  weekCell: {
    flexBasis: '14.2857%',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing.xs,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  cell: {
    flexBasis: '14.2857%',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing.xs,
  },
  dayBox: {
    width: theme.layout.minTapTarget,
    height: theme.layout.minTapTarget,
    borderRadius: theme.radius.pill,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dayBoxSelected: { backgroundColor: theme.color.action.primary },
  dayBoxToday: { backgroundColor: theme.color.focusRing },
});
